import type { UserInvoice } from '@kimuno/api-sdk';
import { palette, spaces } from '@kimuno/theme';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { AsyncActionStatus } from '../../actions/common.actionTypes';
import { getUserInvoices } from '../../actions/user.actions';
import { selectUserInvoices, selectUserInvoicesStatus } from '../../reducers/user.selectors';
import billingService from '../../services/billing.service';
import { useAppDispatch } from '../../store/hooks';
import InvoiceHistoryTable from '../InvoiceHistoryTable';
import initFileDownload from '../utils/downloadIFrame';

const statusLabels: Record<UserInvoice['status'], string> = {
	paid: 'Paid',
	open: 'Unpaid',
	uncollectible: 'Written Off',
};

const sourceLabels: Record<UserInvoice['source'], string> = {
	stripe: 'Stripe',
	zuora: 'Archive',
};

const toTableRow = (invoice: UserInvoice) => ({
	id: invoice.id,
	invoiceDate: invoice.date,
	invoiceNumber: invoice.number ?? '',
	amount: invoice.amountCents / 100,
	status: invoice.isCreditNote ? 'Credit note' : statusLabels[invoice.status],
	sourceLabel: sourceLabels[invoice.source],
	canDownload: invoice.downloadUrl !== null,
});

const StripeInvoiceHistory = () => {
	const { t } = useTranslation();
	const dispatch = useAppDispatch();
	const userInvoices = useSelector(selectUserInvoices);
	const status = useSelector(selectUserInvoicesStatus);
	const [downloadError, setDownloadError] = useState<Error | null>(null);

	useEffect(() => {
		dispatch(getUserInvoices());
	}, [dispatch]);

	const invoices = userInvoices?.invoices ?? [];

	const onClickDownload = async (invoiceId: string) => {
		const invoice = invoices.find((candidate) => candidate.id === invoiceId);
		if (!invoice?.downloadUrl) return;

		setDownloadError(null);
		if (invoice.source === 'stripe') {
			window.open(invoice.downloadUrl, '_blank', 'noopener,noreferrer');
			return;
		}
		if (invoice.number === null) return;

		// A Zuora row's `downloadUrl` is kimUno's route, which the browser cannot
		// authenticate against; the proxy hands out a short-lived signed link instead.
		try {
			const { url } = await billingService.getZuoraInvoicePdfUrl(invoice.number);
			initFileDownload(url);
		} catch (error) {
			setDownloadError(error as Error);
		}
	};

	const renderContent = () => {
		if (userInvoices) {
			return (
				<InvoiceHistoryTable
					invoiceHistory={{ invoices: invoices.map(toTableRow) }}
					onClickDownload={onClickDownload}
					pageSize={20}
					serverError={downloadError}
				/>
			);
		}
		if (status === AsyncActionStatus.ERROR) {
			return <Alert severity="error">{t('Ups, something went wrong')}</Alert>;
		}
		return (
			<Box sx={{ display: 'flex', justifyContent: 'center', paddingY: spaces.lg }}>
				<CircularProgress />
			</Box>
		);
	};

	return (
		<Box>
			<Typography variant="h6" color={palette.text.primary} sx={{ paddingY: spaces.md }}>
				{t('Invoice history')}
			</Typography>
			{renderContent()}
		</Box>
	);
};

export default StripeInvoiceHistory;
